// src/features/documents/components/PreviewPanel.tsx
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkFrontmatter from 'remark-frontmatter';
import { ArrowRight, ArrowLeft, Download, Link as LinkIcon, Link2, Eye, X, Database, Info, FileText, Tag, TextIcon, Wrench, RotateCw, RefreshCw, Loader2, Check, XCircle, ThumbsUp, ThumbsDown, Braces, ListTree, Code, ExternalLink } from 'lucide-react';
import type { PipelineStageState, PipelineStageStatus, PipelineStatus } from '../types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useDocumentDetail, useDocumentPreviewUrl, useDocumentDownloadUrl, useDocumentLinks, useTags, useReconvert, useReembed, useReclassify, useRecordView, useToggleReaction } from '../hooks/useDocuments';
import { StatusBadge } from './StatusBadge';
import type { Document, LinkedDocument, SearchContext } from '../types';
import { driveWebViewLink } from '../api';
import { formatBytes } from '@/lib/utils';

function formatDate(date: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-CA');
}

// --- Pipeline traffic-light dot for the colored stage tabs ---

const STAGE_DOT_CLASSES: Record<PipelineStageStatus | 'idle', string> = {
  pending: 'bg-muted-foreground/30',
  idle: 'bg-muted-foreground/30',
  processing: 'bg-amber-400 animate-pulse',
  done: 'bg-emerald-500',
  failed: 'bg-red-500',
  skipped: 'bg-muted-foreground/40',
};

interface StageDotProps {
  state: PipelineStageState | undefined;
}

function StageDot({ state }: StageDotProps) {
  const status: PipelineStageStatus | 'idle' = state?.status ?? 'idle';
  const tooltip = state?.error
    ? `${status}: ${state.error}`
    : state?.at
      ? `${status} • ${new Date(state.at).toLocaleString()}`
      : status;
  return (
    <span
      title={tooltip}
      className={`inline-block h-2 w-2 rounded-full mr-1.5 shrink-0 ${STAGE_DOT_CLASSES[status]}`}
    />
  );
}

// --- Pipeline step component ---

type StepStatus = 'idle' | 'running' | 'done' | 'error';

interface PipelineStep {
  label: string;
  description: string;
  status: StepStatus;
  estimatedSeconds: number;
}

function StepRow({ step, onRun, disabled }: { step: PipelineStep; onRun: () => void; disabled: boolean }) {
  const icon = step.status === 'running' ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
    : step.status === 'done' ? <Check className="h-4 w-4 text-green-500" />
    : step.status === 'error' ? <XCircle className="h-4 w-4 text-red-500" />
    : <RotateCw className="h-4 w-4 text-muted-foreground" />;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border bg-card">
      <div className="shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{step.label}</p>
        <p className="text-xs text-muted-foreground">{step.description}</p>
        {step.status === 'running' && (
          <div className="mt-2 space-y-1">
            <Progress className="h-1.5" />
            <p className="text-[10px] text-muted-foreground">~{step.estimatedSeconds}s estimated</p>
          </div>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0 h-7 text-xs"
        disabled={disabled || step.status === 'running'}
        onClick={onRun}
      >
        {step.status === 'running' ? 'Running...' : 'Run'}
      </Button>
    </div>
  );
}

function ToolsPanel({
  doc,
  reconvert,
  reembed,
  reclassify,
}: {
  doc: Document;
  reconvert: ReturnType<typeof useReconvert>;
  reembed: ReturnType<typeof useReembed>;
  reclassify: ReturnType<typeof useReclassify>;
}) {
  const anyRunning = reconvert.isPending || reembed.isPending || reclassify.isPending;

  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineStep, setPipelineStep] = useState(0);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [resultTab, setResultTab] = useState<'json' | 'markdown'>('json');

  const steps: PipelineStep[] = [
    {
      label: '1. Reconvert',
      description: "Re-run Docling (Hakim's Mac) to generate new markdown version",
      status: reconvert.isPending || (pipelineRunning && pipelineStep === 0) ? 'running'
        : reconvert.isError ? 'error'
        : reconvert.isSuccess || (pipelineRunning && pipelineStep > 0) ? 'done'
        : 'idle',
      estimatedSeconds: 30,
    },
    {
      label: '2. Re-embed',
      description: 'Regenerate BGE-M3 embedding from new markdown',
      status: reembed.isPending || (pipelineRunning && pipelineStep === 1) ? 'running'
        : reembed.isError ? 'error'
        : reembed.isSuccess || (pipelineRunning && pipelineStep > 1) ? 'done'
        : 'idle',
      estimatedSeconds: 5,
    },
    {
      label: '3. Reclassify',
      description: 'Re-run classification rules and extract structured data',
      status: reclassify.isPending || (pipelineRunning && pipelineStep === 2) ? 'running'
        : reclassify.isError ? 'error'
        : reclassify.isSuccess || (pipelineRunning && pipelineStep > 2) ? 'done'
        : 'idle',
      estimatedSeconds: 10,
    },
  ];

  const runFullPipeline = useCallback(async () => {
    setPipelineRunning(true);
    setPipelineError(null);
    try {
      setPipelineStep(0);
      await reconvert.mutateAsync();
      setPipelineStep(1);
      await reembed.mutateAsync();
      setPipelineStep(2);
      await reclassify.mutateAsync();
      setPipelineStep(3);
      toast.success('Full pipeline complete');
    } catch (e: any) {
      setPipelineError(e.message);
    } finally {
      setPipelineRunning(false);
    }
  }, [reconvert, reembed, reclassify]);

  // Reconvert result data (only after running)
  const reconvertData = reconvert.data as any;
  const reconvertResult = reconvert.isSuccess ? (reconvertData?.data ?? reconvertData ?? null) : null;
  const reconvertMarkdown = reconvertResult?.content ?? null;
  const hasResult = reconvert.isSuccess || reconvert.isError;

  return (
    <div className="p-4 space-y-4">
      <div>
        <p className="text-sm font-medium mb-1">Processing pipeline</p>
        <p className="text-xs text-muted-foreground mb-3">
          Version {doc.currentVersion?.version ?? 1} · {doc.currentVersion?.conversionStatus ?? 'unknown'}
        </p>
      </div>

      <div className="space-y-2">
        {steps.map((step, i) => (
          <StepRow
            key={i}
            step={step}
            disabled={anyRunning || pipelineRunning}
            onRun={() => {
              if (i === 0) reconvert.mutate();
              else if (i === 1) reembed.mutate();
              else reclassify.mutate();
            }}
          />
        ))}
      </div>

      {pipelineError && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 p-3">
          <p className="text-xs text-red-600 dark:text-red-400">{pipelineError}</p>
        </div>
      )}

      <Button
        className="w-full"
        disabled={anyRunning || pipelineRunning}
        onClick={runFullPipeline}
      >
        {pipelineRunning ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Running pipeline...</>
        ) : (
          <><RotateCw className="h-4 w-4 mr-2" />Run full pipeline</>
        )}
      </Button>

      {/* Results section — shown after reconvert runs */}
      {hasResult && (
        <div className="border-t pt-4 space-y-2">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">Result</p>
            <div className="flex gap-1 ml-auto">
              <Button
                variant={resultTab === 'json' ? 'default' : 'outline'}
                size="sm"
                className="h-6 text-[10px] px-2"
                onClick={() => setResultTab('json')}
              >
                Docling JSON
              </Button>
              <Button
                variant={resultTab === 'markdown' ? 'default' : 'outline'}
                size="sm"
                className="h-6 text-[10px] px-2"
                onClick={() => setResultTab('markdown')}
                disabled={!reconvertMarkdown}
              >
                Markdown
              </Button>
            </div>
          </div>

          {resultTab === 'json' && reconvertResult && (
            <pre className="text-xs bg-muted/50 rounded-lg p-3 overflow-auto max-h-[400px] whitespace-pre-wrap font-mono">
              {JSON.stringify({ ...reconvertResult, content: reconvertResult.content ? `[${reconvertResult.content.length} chars]` : null }, null, 2)}
            </pre>
          )}

          {resultTab === 'json' && !reconvertResult && (
            <div className="text-xs text-muted-foreground text-center py-4">
              Run reconvert to see Docling response
            </div>
          )}

          {resultTab === 'markdown' && reconvertMarkdown && (
            <div className="rounded-lg border overflow-auto max-h-[400px]">
              <div className="prose prose-sm max-w-none p-3 [&_table]:border-collapse [&_table]:border [&_table]:border-black [&_th]:border [&_th]:border-black [&_th]:px-2 [&_th]:py-1 [&_th]:bg-gray-100 [&_td]:border [&_td]:border-black [&_td]:px-2 [&_td]:py-1">
                <Markdown remarkPlugins={[remarkGfm, remarkFrontmatter]}>{reconvertMarkdown}</Markdown>
              </div>
            </div>
          )}

          {resultTab === 'markdown' && !reconvertMarkdown && (
            <div className="text-xs text-muted-foreground text-center py-4">
              No markdown content in reconvert result
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Tags panel ---

function TagsPanel({ tagIds }: { tagIds: string[] | null }) {
  const { data: tagMap, isLoading } = useTags();

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-3/4" />
      </div>
    );
  }

  if (!tagIds || tagIds.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 p-10">
        <Tag className="h-8 w-8 opacity-30" />
        <p className="text-sm font-medium">No tags</p>
        <p className="text-xs text-center max-w-[200px]">This document has not been tagged yet.</p>
      </div>
    );
  }

  // Group tags by their group label
  const grouped = new Map<string, { color: string | null; tags: { id: string; label: string; color: string | null }[] }>();
  const ungrouped: { id: string; label: string; color: string | null }[] = [];

  for (const id of tagIds) {
    const info = tagMap?.get(id);
    const tag = { id, label: info?.label ?? id, color: info?.color ?? null };
    if (info?.group) {
      const groupKey = info.group.label;
      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, { color: info.group.color, tags: [] });
      }
      grouped.get(groupKey)!.tags.push(tag);
    } else {
      ungrouped.push(tag);
    }
  }

  return (
    <div className="p-4 space-y-4">
      {[...grouped.entries()].map(([groupLabel, group]) => (
        <div key={groupLabel}>
          <p className="text-xs font-medium text-muted-foreground mb-2">{groupLabel}</p>
          <div className="flex flex-wrap gap-1.5">
            {group.tags.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium"
                style={{
                  backgroundColor: tag.color ? `${tag.color}20` : undefined,
                  color: tag.color ?? undefined,
                  border: `1px solid ${tag.color ?? 'currentColor'}40`,
                }}
              >
                {tag.label}
              </span>
            ))}
          </div>
        </div>
      ))}

      {ungrouped.length > 0 && (
        <div>
          {grouped.size > 0 && <p className="text-xs font-medium text-muted-foreground mb-2">Other</p>}
          <div className="flex flex-wrap gap-1.5">
            {ungrouped.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium"
                style={{
                  backgroundColor: tag.color ? `${tag.color}20` : undefined,
                  color: tag.color ?? undefined,
                  border: `1px solid ${tag.color ?? 'currentColor'}40`,
                }}
              >
                {tag.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Link item component ---

function LinkItem({ link, direction }: { link: LinkedDocument; direction: 'outgoing' | 'incoming' }) {
  const Icon = direction === 'outgoing' ? ArrowRight : ArrowLeft;
  return (
    <div className="flex items-center gap-2 p-2.5 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
      <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{link.document.name}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          {link.linkType && (
            <span
              className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium"
              style={{
                backgroundColor: link.linkType.color ? `${link.linkType.color}20` : undefined,
                color: link.linkType.color ?? undefined,
                border: `1px solid ${link.linkType.color ?? 'currentColor'}40`,
              }}
            >
              {link.linkType.label}
            </span>
          )}
          {link.label && <span className="text-[10px] text-muted-foreground">{link.label}</span>}
        </div>
      </div>
      <span className="text-[10px] text-muted-foreground shrink-0">{formatDate(link.createdAt)}</span>
    </div>
  );
}

// --- Links panel ---

function LinksPanel({ docId }: { docId: string }) {
  const { data, isLoading } = useDocumentLinks(docId);

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  const links = data?.links ?? [];
  const backlinks = data?.backlinks ?? [];
  const total = links.length + backlinks.length;

  if (total === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 p-10">
        <Link2 className="h-8 w-8 opacity-30" />
        <p className="text-sm font-medium">No links</p>
        <p className="text-xs text-center max-w-[200px]">This document is not linked to any other documents.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {links.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Links ({links.length})</p>
          <div className="space-y-1.5">
            {links.map((link) => (
              <LinkItem key={link.id} link={link} direction="outgoing" />
            ))}
          </div>
        </div>
      )}

      {backlinks.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Referenced by ({backlinks.length})</p>
          <div className="space-y-1.5">
            {backlinks.map((link) => (
              <LinkItem key={link.id} link={link} direction="incoming" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Match banner ---

function MatchBanner({ searchContext }: { searchContext: SearchContext }) {
  const isKeyword = searchContext.matchType === 'title' || searchContext.matchType === 'content';
  const label = isKeyword ? 'KEYWORD MATCH' : 'MATCHED PASSAGE';
  const heading = searchContext.matchedChunk?.heading;
  const pages = searchContext.matchedChunk?.pageNumbers;
  const text = searchContext.matchedChunk?.text || searchContext.searchHeadline;

  if (!text) return null;

  return (
    <div className="bg-purple-50 dark:bg-purple-950/20 border-l-[3px] border-purple-500 p-3 mx-3 mt-2 rounded-r-md">
      <div className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 mb-1 flex items-center gap-2 flex-wrap">
        <span>{label}</span>
        {heading && <span className="font-normal text-purple-500">· Section: {heading}</span>}
        {pages && pages.length > 0 && <span className="font-normal text-purple-500">· Page {pages.join(', ')}</span>}
      </div>
      {isKeyword && searchContext.searchHeadline ? (
        <p
          className="text-xs text-foreground/80 leading-relaxed line-clamp-3 [&_mark]:bg-yellow-200 [&_mark]:dark:bg-yellow-800 [&_mark]:rounded-sm [&_mark]:px-0.5"
          dangerouslySetInnerHTML={{ __html: searchContext.searchHeadline }}
        />
      ) : (
        <p className="text-xs text-foreground/80 leading-relaxed line-clamp-3">
          {searchContext.matchedChunk?.text?.slice(0, 300)}
        </p>
      )}
    </div>
  );
}

// --- Preview panel ---

interface PreviewPanelProps {
  doc: Document;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onClose: () => void;
  searchContext?: SearchContext | null;
}

export function PreviewPanel({ doc, activeTab, onTabChange, onClose, searchContext }: PreviewPanelProps) {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['document-detail', doc.id] });
    await queryClient.invalidateQueries({ queryKey: ['documents'] });
    setRefreshing(false);
    toast.success('Refreshed');
  }, [doc.id, queryClient]);

  const mime = doc.currentVersion?.sourceMimeType ?? null;
  const isPreviewable = !!mime && (mime.startsWith('image/') || mime === 'application/pdf');

  const { data: detail, isLoading: detailLoading } = useDocumentDetail(doc.id);
  const { data: preview, isLoading: previewLoading } = useDocumentPreviewUrl(
    doc.id,
    isPreviewable,
  );
  const { data: download } = useDocumentDownloadUrl(
    doc.id,
    !isPreviewable,
  );

  // Pipeline action mutations
  const reconvert = useReconvert(doc.id);
  const reembed = useReembed(doc.id);
  const reclassify = useReclassify(doc.id);

  // Pipeline state + Docling JSON come from the *detail* fetch, not the
  // selected list row — the list endpoint doesn't return doclingJson or
  // pipelineStatus to keep payloads small.
  const detailVersion = (detail?.currentVersion ?? doc.currentVersion) as any;
  const persistedDoclingJson = detailVersion?.doclingJson ?? null;
  const reconvertDoclingJson = reconvert.isSuccess
    ? ((reconvert.data as any)?.data?.doclingJson ?? null)
    : null;
  const doclingJsonResult = reconvertDoclingJson ?? persistedDoclingJson;

  // Per-stage pipeline state for the colored tab dots.
  const pipelineStatus: PipelineStatus = (detailVersion?.pipelineStatus ?? {}) as PipelineStatus;

  // View count — fire once when panel mounts
  const recordView = useRecordView(doc.id);
  const [viewRecorded, setViewRecorded] = useState(false);
  if (!viewRecorded) {
    setViewRecorded(true);
    recordView.mutate();
  }

  // Reactions — optimistic local state so color changes instantly
  const toggleReaction = useToggleReaction(doc.id);
  const serverReaction: 'up' | 'down' | null = (detail as any)?.userReaction ?? null;
  const [localReaction, setLocalReaction] = useState<'up' | 'down' | null>(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const userReaction = hasInteracted ? localReaction : serverReaction;

  const handleReaction = (reaction: 'up' | 'down') => {
    const newReaction = userReaction === reaction ? null : reaction;
    setLocalReaction(newReaction);
    setHasInteracted(true);
    toggleReaction.mutate(reaction);
  };

  const loading = detailLoading || previewLoading;
  const markdownContent = detail?.currentVersion?.content ?? null;
  // Deep link to *this* document inside the DMS UI (shareable, bookmarkable),
  // not the short-lived signed GCS preview URL. The DocumentsPage syncs the
  // `?id=<uuid>` param with selection, so opening this URL re-selects the doc.
  const deepLink = useMemo(
    () => `${window.location.origin}/documents?id=${doc.id}`,
    [doc.id],
  );

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(deepLink).then(() => {
      toast.success('Document link copied');
    });
  }, [deepLink]);

  const handleDownload = useCallback(() => {
    const url = preview?.previewUrl ?? download?.downloadUrl;
    if (url) window.open(url, '_blank');
  }, [preview, download]);

  // Structured data from classification pipeline
  const metadata = doc.metadata;
  const driveUrl = driveWebViewLink(doc);
  const extractionMeta = metadata?._extraction as Record<string, unknown> | undefined;
  const dataFields = useMemo(() => {
    if (!metadata) return null;
    const entries = Object.entries(metadata).filter(([k]) => !k.startsWith('_'));
    return entries.length > 0 ? entries : null;
  }, [metadata]);

  // Resolve effective tab: fall back if current tab's content isn't available
  const hasData = !!dataFields;
  const effectiveTab = activeTab || 'preview';

  return (
    <div className="flex flex-col h-full border-l bg-background">
      {/* Header */}
      <div className="px-4 py-3 border-b flex items-center gap-2 shrink-0">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold truncate">{doc.name}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {formatDate(doc.documentDate)} · {doc.currentVersion?.sourceSize ? formatBytes(doc.currentVersion.sourceSize) : '—'}
          </p>
        </div>
        <Button variant="outline" size="icon" className="h-7 w-7 shrink-0" onClick={handleRefresh} title="Refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
        <Button variant="outline" size="icon" className="h-7 w-7 shrink-0" onClick={handleDownload} title="Download">
          <Download className="h-3.5 w-3.5" />
        </Button>
        <Button variant="outline" size="icon" className="h-7 w-7 shrink-0" onClick={handleCopyLink} title="Copy link">
          <LinkIcon className="h-3.5 w-3.5" />
        </Button>
        {driveUrl && (
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => window.open(driveUrl, '_blank', 'noopener')}
            title="Open in Google Drive"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        )}
        <div className="flex items-center gap-0.5 border-l pl-2 ml-1">
          <Button
            variant="outline"
            size="icon"
            className={`h-7 w-7 shrink-0 transition-colors ${
              userReaction === 'up'
                ? 'bg-green-600 border-green-600 text-white hover:bg-green-700 hover:border-green-700 hover:text-white'
                : 'text-muted-foreground hover:text-green-600 hover:border-green-600'
            }`}
            onClick={() => handleReaction('up')}
            disabled={toggleReaction.isPending}
            title="Thumbs up"
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className={`h-7 w-7 shrink-0 transition-colors ${
              userReaction === 'down'
                ? 'bg-red-500 border-red-500 text-white hover:bg-red-600 hover:border-red-600 hover:text-white'
                : 'text-muted-foreground hover:text-red-500 hover:border-red-500'
            }`}
            onClick={() => handleReaction('down')}
            disabled={toggleReaction.isPending}
            title="Thumbs down"
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </Button>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose} title="Close">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {loading ? (
          <div className="p-6 space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (preview?.previewUrl || markdownContent || hasData || true) ? (
          <>
            {searchContext && <MatchBanner searchContext={searchContext} />}
            <Tabs value={effectiveTab} onValueChange={onTabChange} className="flex-1 flex flex-col min-h-0">
            <TabsList className="mx-4 mt-2 w-fit shrink-0">
              {/* Pipeline stages — left-to-right matches processing order, dots
                  show traffic-light status (gray=pending, yellow=processing,
                  green=done, red=failed). */}
              <TabsTrigger value="preview">
                <StageDot state={pipelineStatus.raw} />
                <FileText className="h-3 w-3 mr-1" />Raw
              </TabsTrigger>
              <TabsTrigger value="json">
                <StageDot state={pipelineStatus.doclingJson} />
                <Braces className="h-3 w-3 mr-1" />JSON
              </TabsTrigger>
              <TabsTrigger value="markdown">
                <StageDot state={pipelineStatus.markdown} />
                <TextIcon className="h-3 w-3 mr-1" />Markdown
              </TabsTrigger>
              <TabsTrigger value="html">
                <StageDot state={pipelineStatus.markdown} />
                <Code className="h-3 w-3 mr-1" />HTML
              </TabsTrigger>
              <TabsTrigger value="data">
                <StageDot state={pipelineStatus.structured} />
                <Database className="h-3 w-3 mr-1" />Data
              </TabsTrigger>
              <TabsTrigger value="classification">
                <StageDot state={pipelineStatus.classification} />
                <ListTree className="h-3 w-3 mr-1" />Classification
              </TabsTrigger>
              <TabsTrigger value="tags">
                <StageDot state={pipelineStatus.tags} />
                <Tag className="h-3 w-3 mr-1" />Tags
              </TabsTrigger>
              {/* Auxiliary tabs (no pipeline status) */}
              <TabsTrigger value="links">
                <Link2 className="h-3 w-3 mr-1" />Links
              </TabsTrigger>
              <TabsTrigger value="tools">
                <Wrench className="h-3 w-3 mr-1" />Tools
              </TabsTrigger>
              <TabsTrigger value="metadata">
                <Info className="h-3 w-3 mr-1" />Metadata
              </TabsTrigger>
            </TabsList>

            <TabsContent value="preview" className="flex-1 mt-0 min-h-0">
              {previewLoading ? (
                <div className="p-6 space-y-3">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-40 w-full" />
                </div>
              ) : preview?.previewUrl ? (
                preview.mimeType === 'application/pdf' ? (
                  <iframe
                    src={`${preview.previewUrl}${searchContext?.matchedChunk?.pageNumbers?.[0] ? `#page=${searchContext.matchedChunk.pageNumbers[0]}` : ''}`}
                    title="Document preview"
                    className="w-full h-full border-none"
                  />
                ) : (
                  <div className="p-4 overflow-auto h-full">
                    <img
                      src={preview.previewUrl}
                      alt={doc.name}
                      className="max-w-full rounded"
                    />
                  </div>
                )
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 p-10">
                  <Eye className="h-8 w-8 opacity-30" />
                  <p className="text-sm font-medium">No preview</p>
                  <p className="text-xs text-center max-w-[200px]">Preview not available for this document type.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="markdown" className="flex-1 mt-0 overflow-auto min-h-0">
              {detailLoading ? (
                <div className="p-6 space-y-3">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-40 w-full" />
                </div>
              ) : markdownContent ? (
                <div className="prose prose-sm max-w-none p-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_li>p]:my-0 [&_img]:max-w-full [&_img]:h-auto [&_table]:border-collapse [&_table]:border [&_table]:border-black [&_th]:border [&_th]:border-black [&_th]:px-2 [&_th]:py-1 [&_th]:bg-gray-100 [&_td]:border [&_td]:border-black [&_td]:px-2 [&_td]:py-1">
                  <Markdown
                    remarkPlugins={[remarkGfm, remarkFrontmatter]}
                    // react-markdown v10 strips `data:` URIs by default as a
                    // safety measure. Docling embeds page images as
                    // `data:image/png;base64,...` directly in the markdown
                    // when `image_export_mode=embedded`, and the content
                    // comes from our own trusted converter, so we allow
                    // `data:image/*` through. Everything else is left to the
                    // library default by returning an empty string for
                    // unsupported protocols.
                    urlTransform={(value) => {
                      if (value.startsWith('data:image/')) return value;
                      if (/^(https?:|mailto:|tel:|#|\/|\.\/|\.\.\/)/.test(value)) return value;
                      return '';
                    }}
                  >{markdownContent}</Markdown>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 p-10">
                  <TextIcon className="h-8 w-8 opacity-30" />
                  <p className="text-sm font-medium">No markdown</p>
                  <p className="text-xs text-center max-w-[200px]">This document has not been converted yet.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="html" className="flex-1 mt-0 min-h-0">
              {detailVersion?.htmlContent ? (
                <iframe
                  title="Docling HTML rendering"
                  srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>
                    body{font:13px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#111;padding:16px;margin:0}
                    h1,h2,h3,h4{line-height:1.25;margin-top:1.2em}
                    table{border-collapse:collapse;margin:8px 0}
                    th,td{border:1px solid #000;padding:4px 8px;text-align:left}
                    th{background:#f3f4f6}
                    img{max-width:100%;height:auto}
                    pre{background:#f3f4f6;padding:8px;border-radius:4px;overflow:auto}
                    code{background:#f3f4f6;padding:1px 4px;border-radius:3px}
                    a{color:#2563eb}
                  </style></head><body>${detailVersion.htmlContent}</body></html>`}
                  className="w-full h-full border-none"
                  // sandbox isolates JS/top-navigation; allow-same-origin is
                  // not set on purpose so any scripts Docling emits can't
                  // touch our app or cookies.
                  sandbox=""
                />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 p-10">
                  <Code className="h-8 w-8 opacity-30" />
                  <p className="text-sm font-medium">No HTML rendering</p>
                  <p className="text-xs text-center max-w-[240px]">
                    {pipelineStatus.markdown?.status === 'failed'
                      ? `Conversion failed: ${pipelineStatus.markdown.error ?? 'unknown error'}`
                      : pipelineStatus.markdown?.status === 'processing'
                        ? 'Converting via Docling — refresh in a few seconds.'
                        : 'HTML is generated automatically after upload. Older documents may need a reconvert from the Tools tab.'}
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="data" className="flex-1 mt-0 overflow-auto min-h-0">
              {hasData ? (
                <div className="p-4 space-y-4">
                  <div className="rounded-lg border">
                    <table className="w-full text-sm">
                      <tbody>
                        {dataFields!.map(([key, value]) => (
                          <tr key={key} className="border-b last:border-b-0">
                            <td className="px-3 py-2 font-medium text-muted-foreground bg-muted/30 w-1/3 align-top">
                              {key.replace(/_/g, ' ')}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">
                              {typeof value === 'number' ? (
                                <span className="text-blue-600 dark:text-blue-400">
                                  {value.toLocaleString('en-CA', value % 1 !== 0 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {})}
                                </span>
                              ) : typeof value === 'boolean' ? (
                                <span className={value ? 'text-green-600' : 'text-red-600'}>{String(value)}</span>
                              ) : value === null || value === undefined ? (
                                <span className="text-muted-foreground">—</span>
                              ) : typeof value === 'object' ? (
                                <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(value, null, 2)}</pre>
                              ) : (
                                <span>{String(value)}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {extractionMeta && (
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p className="font-medium">Extraction info</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                        {!!extractionMeta.ruleName && <><span>Rule</span><span className="font-mono">{String(extractionMeta.ruleName)}</span></>}
                        {!!extractionMeta.extractedAt && <><span>Extracted</span><span>{formatDate(String(extractionMeta.extractedAt))}</span></>}
                        {extractionMeta.valid !== undefined && <><span>Valid</span><span>{extractionMeta.valid ? 'Yes' : 'No'}</span></>}
                        {extractionMeta.cost !== undefined && <><span>Cost</span><span>${Number(extractionMeta.cost).toFixed(5)}</span></>}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 p-10">
                  <Database className="h-8 w-8 opacity-30" />
                  <p className="text-sm font-medium">No structured data</p>
                  <p className="text-xs text-center max-w-[200px]">This document has not been classified or has no extracted fields.</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="classification" className="flex-1 mt-0 overflow-auto min-h-0">
              {extractionMeta?.ruleName ? (
                <div className="p-4 space-y-3">
                  <div className="rounded-lg border">
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b">
                          <td className="px-3 py-2 text-muted-foreground bg-muted/30 w-1/3">Matched rule</td>
                          <td className="px-3 py-2 font-mono text-xs">{String(extractionMeta.ruleName)}</td>
                        </tr>
                        {!!extractionMeta.extractedAt && (
                          <tr className="border-b">
                            <td className="px-3 py-2 text-muted-foreground bg-muted/30">Classified at</td>
                            <td className="px-3 py-2">{formatDate(String(extractionMeta.extractedAt))}</td>
                          </tr>
                        )}
                        {extractionMeta.valid !== undefined && (
                          <tr className="border-b">
                            <td className="px-3 py-2 text-muted-foreground bg-muted/30">Valid</td>
                            <td className="px-3 py-2">{extractionMeta.valid ? 'Yes' : 'No'}</td>
                          </tr>
                        )}
                        {extractionMeta.cost !== undefined && (
                          <tr>
                            <td className="px-3 py-2 text-muted-foreground bg-muted/30">LLM cost</td>
                            <td className="px-3 py-2">${Number(extractionMeta.cost as number).toFixed(5)}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {pipelineStatus.classification?.error && (
                    <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 p-3 text-xs text-red-800 dark:text-red-300">
                      <p className="font-medium mb-1">Classification error</p>
                      <pre className="whitespace-pre-wrap">{pipelineStatus.classification.error}</pre>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 p-10">
                  <ListTree className="h-8 w-8 opacity-30" />
                  <p className="text-sm font-medium">Not classified yet</p>
                  <p className="text-xs text-center max-w-[240px]">
                    {pipelineStatus.classification?.status === 'failed'
                      ? `Classification failed: ${pipelineStatus.classification.error ?? 'unknown error'}`
                      : 'Classification runs automatically after conversion. Use the Tools tab to re-run it.'}
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="tags" className="flex-1 mt-0 overflow-auto min-h-0">
              <TagsPanel tagIds={doc.tagIds} />
            </TabsContent>

            <TabsContent value="links" className="flex-1 mt-0 overflow-auto min-h-0">
              <LinksPanel docId={doc.id} />
            </TabsContent>

            <TabsContent value="metadata" className="flex-1 mt-0 overflow-auto min-h-0">
              <div className="p-4 space-y-4">
                {/* Document info */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Document</p>
                  <div className="rounded-lg border">
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30 w-1/3">ID</td><td className="px-3 py-1.5 font-mono text-xs">{doc.id}</td></tr>
                        <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Name</td><td className="px-3 py-1.5">{doc.name}</td></tr>
                        <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Document date</td><td className="px-3 py-1.5">{formatDate(doc.documentDate)}</td></tr>
                        <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Created</td><td className="px-3 py-1.5">{formatDate(doc.createdAt)}</td></tr>
                        <tr><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Updated</td><td className="px-3 py-1.5">{formatDate(doc.updatedAt)}</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Version info */}
                {doc.currentVersion && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Current version</p>
                    <div className="rounded-lg border">
                      <table className="w-full text-sm">
                        <tbody>
                          <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30 w-1/3">Version</td><td className="px-3 py-1.5">{doc.currentVersion.version}</td></tr>
                          <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Original name</td><td className="px-3 py-1.5 text-xs break-all">{doc.currentVersion.originalName}</td></tr>
                          <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">MIME type</td><td className="px-3 py-1.5 font-mono text-xs">{doc.currentVersion.sourceMimeType ?? '—'}</td></tr>
                          <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Size</td><td className="px-3 py-1.5">{doc.currentVersion.sourceSize ? formatBytes(doc.currentVersion.sourceSize) : '—'}</td></tr>
                          <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Conversion</td><td className="px-3 py-1.5">{doc.currentVersion.conversionStatus ?? '—'}</td></tr>
                          <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Pages</td><td className="px-3 py-1.5">{doc.currentVersion.pageCount ?? '—'}</td></tr>
                          <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Words</td><td className="px-3 py-1.5">{doc.currentVersion.wordCount?.toLocaleString() ?? '—'}</td></tr>
                          <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Language</td><td className="px-3 py-1.5">{doc.currentVersion.language ?? '—'}</td></tr>
                          {doc.currentVersion.uploadSource && <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Upload source</td><td className="px-3 py-1.5">{doc.currentVersion.uploadSource}</td></tr>}
                          {doc.currentVersion.sourcePath && <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Source path</td><td className="px-3 py-1.5 text-xs break-all">{doc.currentVersion.sourcePath}</td></tr>}
                          {driveUrl && <tr className="border-b"><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Google Drive</td><td className="px-3 py-1.5 text-xs break-all"><a href={driveUrl} target="_blank" rel="noopener noreferrer" className="text-primary underline">{driveUrl}</a></td></tr>}
                          <tr><td className="px-3 py-1.5 text-muted-foreground bg-muted/30">Created</td><td className="px-3 py-1.5">{formatDate(doc.currentVersion.createdAt)}</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Pipeline status */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Pipeline</p>
                  <div className="rounded-lg border">
                    <table className="w-full text-sm">
                      <tbody>
                        {(['raw', 'doclingJson', 'markdown', 'structured', 'classification', 'tags'] as const).map((stage) => {
                          const state = pipelineStatus[stage];
                          return (
                            <tr key={stage} className="border-b last:border-b-0">
                              <td className="px-3 py-1.5 text-muted-foreground bg-muted/30 w-1/3 capitalize">
                                <StageDot state={state} />
                                {stage === 'doclingJson' ? 'Docling JSON' : stage}
                              </td>
                              <td className="px-3 py-1.5">
                                <span className="text-xs">
                                  {state?.status ?? 'pending'}
                                  {state?.at && <span className="text-muted-foreground"> · {formatDate(state.at)}</span>}
                                </span>
                                {state?.error && (
                                  <div className="text-xs text-red-600 dark:text-red-400 mt-0.5 break-all">
                                    {state.error}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Tags */}
                {doc.tagIds && doc.tagIds.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Tags ({doc.tagIds.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {doc.tagIds.map((id) => (
                        <span key={id} className="inline-block px-2 py-0.5 rounded text-xs bg-muted font-mono">{id}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Raw extraction metadata */}
                {extractionMeta && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">Extraction</p>
                    <pre className="text-xs bg-muted/50 rounded-lg p-3 overflow-auto whitespace-pre-wrap">
                      {JSON.stringify(extractionMeta, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="json" className="flex-1 mt-0 overflow-auto min-h-0">
              {doclingJsonResult ? (
                <pre className="text-xs p-4 font-mono whitespace-pre-wrap">
                  {JSON.stringify(doclingJsonResult, null, 2)}
                </pre>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 p-10">
                  <Braces className="h-8 w-8 opacity-30" />
                  <p className="text-sm font-medium">No Docling JSON</p>
                  <p className="text-xs text-center max-w-[240px]">
                    {pipelineStatus.doclingJson?.status === 'failed'
                      ? `Conversion failed: ${pipelineStatus.doclingJson.error ?? 'unknown error'}`
                      : pipelineStatus.doclingJson?.status === 'processing'
                        ? 'Converting via Docling — refresh in a few seconds.'
                        : 'Docling JSON is generated automatically after upload. If this stays empty, re-run conversion from the Tools tab.'}
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="tools" className="flex-1 mt-0 overflow-auto min-h-0">
              <ToolsPanel
                doc={doc}
                reconvert={reconvert}
                reembed={reembed}
                reclassify={reclassify}
              />
            </TabsContent>
          </Tabs>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 p-6">
            <Eye className="h-6 w-6 opacity-40" />
            <p className="text-sm">No preview available</p>
            <Button variant="outline" size="sm" onClick={handleDownload}>
              Download original
            </Button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t flex items-center justify-between text-xs text-muted-foreground shrink-0">
        <span className="inline-flex items-center gap-3">
          <span>Created {formatDate(doc.createdAt)}</span>
          <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{doc.viewCount} views</span>
        </span>
        <StatusBadge status={doc.currentVersion?.conversionStatus ?? null} />
      </div>
    </div>
  );
}
